import { mapValues } from 'lodash';
import moment from 'moment';

const REQUIRED_ERROR = 'Required';
const LENGTH_ERROR = `Can't exceed 50 characters`;
const SPACES_ERROR = `Cannot contain spaces`;
const SPECIAL_CHAR_ERROR =
  'Can include only numbers, letters and the following special characters : _ - < >';
const SPECIAL_CHAR_WITH_DOTS_ERROR =
  'Can include only numbers, letters and the following special characters : _ - < > .';

/**
 * fix site code user input
 * @description convert user input to Airport Code + 3 digit format
 * @param siteCode site code to fix
 */
export function fixSiteCode(siteCode: string): string {
  const matches = /^([A-Za-z]{3})(\d{1,3})$/.exec(siteCode);
  if (matches && matches[1] && matches[2]) {
    return `${matches[1].toUpperCase()}${matches[2].padStart(3, '0')}`;
  }
  return '';
}

/**
 * @description normalize the translation key to transform error code and
 * other string to translation key friendly string
 * eg: SITE_CODE will be normalized to site-code
 * @param key ket to normalize
 */
export function normalizeTranslationKey(key = ''): string {
  return key.toLowerCase().replace(/_/g, '-');
}

/**
 * checks if object has non null values, or empty form entries
 * @example
 * {} - returns false
 * { key: "" } - returns false
 * { key: { value: "" } } - returns false
 * @param {object} obj
 */
export function hasValues(obj: Record<string, unknown>): boolean {
  return (
    obj &&
    Object.values(obj).some(
      (v) =>
        (typeof v !== 'object' && v != null && v !== '') ||
        /* eslint-disable  @typescript-eslint/no-explicit-any */
        (typeof v === 'object' && (v as any)?.value != null && (v as any)?.value !== '')
    )
  );
}

export function getCurrentTimestamp(): number {
  return Math.round(Date.now() / 1000);
}

export function mapEmptyStringParametersToNull(object: Record<string, any>): Record<string, any> {
  return mapValues(object, (value: any) => {
    if (value === '') return null;
    return value;
  });
}

export function humanizeDate(date: Date | string | number | null, format?: string): string {
  if (date) {
    return moment(date)
      .format(format || 'MMM DD, YYYY')
      .valueOf();
  }
  return '';
}

export function isElementScrolledToBottom(element: HTMLElement): boolean {
  return element.scrollTop === element.scrollHeight - element.offsetHeight;
}

export function isElementScrolledNearTop(element: HTMLElement, threshold: number): boolean {
  return element.scrollTop <= threshold;
}

export const isObjectDefined = (obj: any): boolean => {
  return typeof obj !== 'undefined' && obj !== null;
};

// checks for special characters
const checkForSpecialChar = (str: string) => {
  // eslint-disable-next-line no-useless-escape
  const spChars = /[!@#$%^&*()+\=\[\]{};':"\\|,.\/?]+/;
  return spChars.test(str);
};

// checks for special characters
const checkForSpecialCharWithDots = (str: string) => {
  // eslint-disable-next-line no-useless-escape
  const spChars = /[!@#$%^&*()+\=\[\]{};':"\\|,\/?]+/;
  return spChars.test(str);
};

// checks for space
const checkForSpace = (str: string) => {
  return /\s/.test(str);
};

// validates input fields
export const validateInput = (value: string, required: boolean, validate: boolean) => {
  if (required && value === '') {
    return REQUIRED_ERROR;
  }
  if (validate) {
    if (value?.length > 50) {
      return LENGTH_ERROR;
    }
    if (checkForSpecialChar(value)) {
      return SPECIAL_CHAR_ERROR;
    }
    if (checkForSpace(value)) {
      return SPACES_ERROR;
    }
  }
  return '';
};

export const validateInputWithDots = (
  value: string,
  required: boolean,
  maxLength: number
): string => {
  if (value?.length > maxLength) {
    return `Can't exceed ${maxLength} characters`;
  }
  if (checkForSpecialCharWithDots(value)) {
    return SPECIAL_CHAR_WITH_DOTS_ERROR;
  }
  if (checkForSpace(value)) {
    return SPACES_ERROR;
  }
  return '';
};

// Gets all the keys of an object. Recursively traverse nested objects and
// return their keys as flattened strings (e.g. "a.b" for { a: { b: 0 } }).
export const getFlattenedKeys = (obj: any, prefix = '') =>
  Object.keys(obj)
    // Recursively get keys if obj[key] is an object
    .map((key) =>
      typeof obj[key] === 'object' && obj[key] != null && !Array.isArray(obj[key])
        ? getFlattenedKeys(obj[key], `${prefix + key}.`)
        : [prefix + key]
    )
    // Concat all the arrays together
    .reduce((result, arr) => result.concat(arr), []);
